import Vue from 'vue';
import { ref, Ref } from '@vue/composition-api';
import orderBy from 'lodash/orderBy';
import { useStorymapStore } from '../useModule';
import { Subtask } from '../types';
import { isHideUnfilteredActive, matchingSubtaskIdSet, getVisibleTasksForNav } from './useHideUnfiltered';
import { getTaskRelease } from './useTaskRelease';
import { isDragInProgress } from '@/_core/app-components/dragInProgress';

/*
 * Decides which parts of a large board are mounted. A region is a release body or the lanes of an activity. It is resident when it is
 * near the viewport, holds the active, new or edited card or a neighbour the arrow keys would need, or was asked for by a reveal.
 *
 * Regions mount at most one per frame, and regions already passed are skipped. Nothing unmounts during a pointer press or a drag,
 * because destroying a Sortable ends the running drag, nor while a menu or dialog is open, because it may belong to a card in the region.
 *
 * Boards below LARGE_MAP_CARDS do none of this: every region is resident.
 */
const LARGE_MAP_CARDS = 300;
const OVERRIDE_KEY = '_storiesonboard_lazyBoard';

const RELEASE_MARGIN = '800px 0px';
const ACTIVITY_MARGIN = '0px 600px';
const EVICT_AFTER_MS = 1000;
const REVEAL_HOLD_MS = 3000;
const OVERLAY_RETRY_MS = 300;
const HEIGHT_SAMPLE_DELAY_MS = 1000;
const DEFAULT_CARD_HEIGHT = 94;
const HEIGHT_SAMPLE_SIZE = 40;
const CARD_GAP = 8;
const BODY_TOP_PADDING = 8;
const LANE_ADD_ROW = 20;
const LANE_MEASURE_DELAY_MS = 500;

// A menu or dialog may belong to a card in a region, so nothing unmounts while one is open. The board's own panels (filter,
// multiselect, search) work on the store, stay open while the user scrolls, and are marked so that they do not count.
const OPEN_OVERLAY = '.menuable__content__active:not(.board-panel-menu), .v-dialog__content--active';
const DRAG_PLACEHOLDER_ACTIVITY = 'activity|placeholder';

export interface Region {
    key: string;
    resident: Ref<boolean>;
    // An activity's header width.
    width: Ref<number>;
    // A release body's expected height: exact for lanes seen with their current number of cards, estimated for the others.
    bodyHeight: Ref<number>;
    near: boolean;
    farSince: number;
    element: Element | null;
}

const regions = new Map<string, Region>();
const anchors = new Set<string>();
const reveals = new Map<string, number>();
export const averageCardHeight = ref(DEFAULT_CARD_HEIGHT);

let lazy = false;
let active = false;
let generation = 0;
let releaseObserver: IntersectionObserver = null;
let activityObserver: IntersectionObserver = null;
let widthObserver: ResizeObserver = null;
let watchers: Vue = null;
let pointerPressed = false;
let printing = false;
let frame: number = null;
let retryTimer: ReturnType<typeof setTimeout> = null;
let sampleTimer: ReturnType<typeof setTimeout> = null;
let scroller: Element = null;
// Card count of each visible lane, by `${releaseId}|${taskId}`, and lanes by release.
let laneCounts: Record<string, number> = {};
let lanesByRelease: Record<string, string[]> = {};
type LaneHeights = Map<string, { count: number; height: number }>;
let laneHeights: LaneHeights = new Map();
// Measured heights by layout (zoom, view, view options), kept for the map across boards: a view switch creates a new board, and coming
// back to a view finds its lanes measured. A lane that changed meanwhile is measured again when it mounts.
const laneHeightsByLayout = new Map<string, LaneHeights>();
let laneHeightsMapId: string = null;
let laneMeasureTimer: ReturnType<typeof setTimeout> = null;
// Watches the card lists of mounted lanes, so that a lane changing height without changing its number of cards (a longer title, a
// filter showing other cards, a font change) is measured again.
let laneResizeObserver: ResizeObserver = null;
const observedLanes = new Set<Element>();

let decidedFor: string = null;
let decidedLazy = false;

const hasObservers = () => typeof IntersectionObserver === 'function' && typeof ResizeObserver === 'function';

const readOverride = (): boolean | null => {
    try {
        const value = localStorage.getItem(OVERRIDE_KEY);
        return value === 'on' ? true : value === 'off' ? false : null;
    } catch (e) {
        return null;
    }
};

/** Decided when a map is first shown. See reconsiderLazyBoard. */
export const isLazyBoardEnabled = (): boolean => {
    const state = useStorymapStore().state;
    if (decidedFor !== state.id) {
        decidedFor = state.id;
        decidedLazy = hasObservers() && (readOverride() ?? state.subtasks.length >= LARGE_MAP_CARDS);
    }
    return decidedLazy;
};

/** A map that has grown past the limit moves up the next time a board is created, and never back down. */
const reconsiderLazyBoard = () => {
    const state = useStorymapStore().state;
    isLazyBoardEnabled();
    if (!decidedLazy && hasObservers() && readOverride() === null && state.subtasks.length >= LARGE_MAP_CARDS) {
        decidedLazy = true;
    }
};

export const getRegion = (key: string): Region => {
    let region = regions.get(key);
    if (!region) {
        region = {
            key,
            resident: ref(!lazy || key === DRAG_PLACEHOLDER_ACTIVITY),
            width: ref(0),
            bodyHeight: ref(BODY_TOP_PADDING + LANE_ADD_ROW),
            near: false,
            farSince: 0,
            element: null,
        };
        regions.set(key, region);
    }
    return region;
};

const isRevealed = (key: string) => (reveals.get(key) || 0) > Date.now();
const isWanted = (region: Region) => region.near || anchors.has(region.key) || isRevealed(region.key) || region.key === DRAG_PLACEHOLDER_ACTIVITY;
// The store's flag alone is not enough: a drag whose list was destroyed never clears it.
const isDragging = () => useStorymapStore().state.isDraggingCard && isDragInProgress();
const mustKeepEverything = () => printing || pointerPressed || isDragging() || !!document.querySelector(OPEN_OVERLAY);

/** A region holding the focused element stays, whether or not that element is the selected card. */
const holdsFocus = (region: Region) => {
    const focused = document.activeElement;
    if (!focused || focused === document.body) return false;
    if (region.key.startsWith('release|')) return !!region.element && region.element.contains(focused);
    // An activity's lanes are spread over the releases; the lane says which task, and so which activity, it belongs to.
    const lane = focused.closest('[data-hide-task]');
    if (!lane) return false;
    const taskId = lane.getAttribute('data-hide-task');
    const task = useStorymapStore().state.tasks.find((x) => x.id === taskId);
    return !!task && region.key === `activity|${task.activityId}`;
};

const retryLater = (delay: number) => {
    if (retryTimer === null) {
        retryTimer = setTimeout(() => {
            retryTimer = null;
            schedule();
        }, delay);
    }
};

const updateBodyHeight = (releaseId: string) => {
    let tallest = LANE_ADD_ROW;
    for (const lane of lanesByRelease[releaseId] || []) {
        const count = laneCounts[lane];
        const known = laneHeights.get(lane);
        const height = known && known.count === count ? known.height : count * averageCardHeight.value + LANE_ADD_ROW;
        tallest = Math.max(tallest, height);
    }
    const region = regions.get(`release|${releaseId}`);
    if (region && region.bodyHeight.value !== BODY_TOP_PADDING + tallest) {
        region.bodyHeight.value = BODY_TOP_PADDING + tallest;
    }
};

const updateAllBodyHeights = () => {
    for (const region of regions.values()) {
        if (region.key.startsWith('release|')) updateBodyHeight(region.key.slice('release|'.length));
    }
};

/** Remembers the natural height of the lanes under an element, so that they can be left out exactly. */
const measureLanes = (root: Element) => {
    const changed = new Set<string>();
    const releases = root.matches('.release-container') ? [root] : Array.from(root.querySelectorAll('.release-container'));
    for (const element of releases) {
        const releaseId = element.id.slice('releaseId-'.length);
        for (const lane of element.querySelectorAll('.task-release-containter[data-hide-task]')) {
            const key = `${releaseId}|${lane.getAttribute('data-hide-task')}`;
            const list = lane.querySelector('.subtasks-container') as HTMLElement;
            if (!list || !(key in laneCounts)) continue;
            if (laneResizeObserver && !observedLanes.has(list)) {
                observedLanes.add(list);
                laneResizeObserver.observe(list);
            }
            const height = list.offsetHeight + LANE_ADD_ROW;
            const known = laneHeights.get(key);
            if (!known || known.height !== height || known.count !== laneCounts[key]) {
                laneHeights.set(key, { count: laneCounts[key], height });
                changed.add(releaseId);
            }
        }
    }
    return changed;
};

/** Lists of lanes that were unmounted are not watched any longer. */
const forgetUnmountedLanes = () => {
    for (const list of observedLanes) {
        if (!list.isConnected) {
            observedLanes.delete(list);
            laneResizeObserver?.unobserve(list);
        }
    }
};

const measureMountedLanesNow = () => {
    if (!active || !scroller || isDragging()) return;
    const changed = measureLanes(scroller);
    if (changed.size) withScrollAnchor(() => changed.forEach(updateBodyHeight));
};

const measureMountedLanesSoon = () => {
    if (laneMeasureTimer !== null || !active) return;
    laneMeasureTimer = setTimeout(() => {
        laneMeasureTimer = null;
        if (!active || !scroller) return;
        forgetUnmountedLanes();
        // Dragging makes lanes grow, so lanes are only measured at rest. A measurement that has to wait is not dropped.
        if (mustKeepEverything()) {
            measureMountedLanesSoon();
            return;
        }
        const changed = measureLanes(scroller);
        if (changed.size) withScrollAnchor(() => changed.forEach(updateBodyHeight));
    }, LANE_MEASURE_DELAY_MS);
};

/** The selected card of the board, outside a pinned release, when any of it can be seen: the anchor that matters most. */
const visibleSelectedCard = (area: DOMRect): HTMLElement | null => {
    const card = scroller.querySelector('.subtask-card.active') as HTMLElement;
    if (!card || card.closest('.pinnedReleaseContainer')) return null;
    const rect = card.getBoundingClientRect();
    return rect.bottom > area.top && rect.top < area.bottom && rect.right > area.left && rect.left < area.right ? card : null;
};

/**
 * Keeps what the user is looking at in place while regions mount, unmount or change height above it: the selected card when it can
 * be seen, otherwise what is under the middle of the board. The browser's own scroll anchoring is switched off on large boards: it
 * picks another element, and would add its correction to this one.
 */
/**
 * Safari jumped by 100-300 px while scrolling a large board without a wheel listener on the scroller, and did not with one
 * (observed several times, each after a fresh reload; why is not established: WebKit scrolls on its own thread, and a listener may
 * change how that thread and the corrections below are kept in step). Passive, so it does not hold up scrolling anywhere.
 */
const onUserScrollInput = (): void => undefined;

const withScrollAnchor = (change: () => void) => {
    if (!scroller || typeof document.elementFromPoint !== 'function') {
        change();
        return;
    }
    const area = scroller.getBoundingClientRect();
    const probe = document.elementFromPoint(area.left + area.width / 2, area.top + Math.min(area.height * 0.6, 450));
    const underProbe = probe && (probe.closest('.subtask-card, .release-title-container, .release-body-placeholder, .activity-release-placeholder') as HTMLElement);
    // A pinned release stays where it is while the board scrolls, so nothing in it can tell what the change moved.
    const anchor = visibleSelectedCard(area) || (underProbe && !underProbe.closest('.pinnedReleaseContainer') ? underProbe : null);
    const before = anchor ? anchor.getBoundingClientRect().top : 0;
    const target = scroller;
    const scrolledBefore = target.scrollTop;
    change();
    if (!anchor) return;
    Vue.nextTick(() => {
        if (!anchor.isConnected || scroller !== target) return;
        // What the change moved, leaving out any scrolling in between.
        const moved = anchor.getBoundingClientRect().top - before + (target.scrollTop - scrolledBefore);
        if (Math.abs(moved) > 0.5) target.scrollTop += moved;
    });
};

const release = (region: Region) => {
    if (region.element) {
        const lanesRoot = region.key.startsWith('release|') ? region.element : scroller;
        if (lanesRoot) measureLanes(lanesRoot).forEach(updateBodyHeight);
    }
    region.resident.value = false;
};

const run = () => {
    frame = null;
    if (!active) return;
    // One region per frame, and mounting what is wanted comes before releasing what is not.
    for (const region of regions.values()) {
        if (!region.resident.value && isWanted(region)) {
            withScrollAnchor(() => {
                region.resident.value = true;
            });
            // Right after it is laid out: a region passed quickly may be gone before any later measurement.
            Vue.nextTick(measureMountedLanesNow);
            schedule();
            return;
        }
    }
    const now = Date.now();
    let waitMs = 0;
    for (const region of regions.values()) {
        if (region.resident.value && !isWanted(region) && !holdsFocus(region)) {
            if (mustKeepEverything()) {
                retryLater(OVERLAY_RETRY_MS);
                return;
            }
            if (now - region.farSince < EVICT_AFTER_MS) {
                waitMs = EVICT_AFTER_MS;
                continue;
            }
            withScrollAnchor(() => release(region));
            schedule();
            return;
        }
    }
    if (waitMs) {
        retryLater(waitMs);
    }
};

const schedule = () => {
    if (frame === null && active) {
        frame = requestAnimationFrame(run);
    }
};

const elementRegions = new WeakMap<Element, Region>();

const onIntersections = (entries: IntersectionObserverEntry[]) => {
    const now = Date.now();
    for (const entry of entries) {
        const region = elementRegions.get(entry.target);
        if (!region) continue;
        region.near = entry.isIntersecting;
        if (!entry.isIntersecting) region.farSince = now;
    }
    schedule();
};

const observe = (region: Region, element: Element) => {
    if (region.key.startsWith('release|')) {
        releaseObserver.observe(element);
    } else {
        activityObserver.observe(element);
        widthObserver.observe(element);
    }
};

const stopObserving = (element: Element) => {
    releaseObserver?.unobserve(element);
    activityObserver?.unobserve(element);
    widthObserver?.unobserve(element);
};

export const registerRegionElement = (key: string, element: Element) => {
    if (!lazy) return;
    const region = getRegion(key);
    if (region.element && region.element !== element && active) {
        stopObserving(region.element);
    }
    region.element = element;
    elementRegions.set(element, region);
    if (active) observe(region, element);
};

/** Takes the element too: a new owner may have registered the region already. */
export const unregisterRegionElement = (key: string, element: Element) => {
    if (!lazy) return;
    const region = regions.get(key);
    if (!region || region.element !== element) return;
    if (active) stopObserving(element);
    elementRegions.delete(element);
    region.element = null;
    region.near = false;
    region.farSince = 0;
    schedule();
};

/** A card about to be selected or flashed from elsewhere: its regions mount now, without waiting for a frame. */
export const revealCardRegions = (releaseId: string | null, activityId: string) => {
    if (!active) return;
    const until = Date.now() + REVEAL_HOLD_MS;
    for (const key of [`release|${releaseId}`, `activity|${activityId}`]) {
        reveals.set(key, until);
        getRegion(key).resident.value = true;
    }
    retryLater(REVEAL_HOLD_MS + EVICT_AFTER_MS);
};

const findCardElement = (cardId: string) =>
    document.getElementById(`subtask-${cardId}`) || document.getElementById(`task-${cardId}`) || document.getElementById(`activity-${cardId}`);

/**
 * Resolves with null when the element does not appear in time, or when the board has gone in the meantime. A board without lazy
 * regions renders an element by the next tick, so it is not waited for there.
 */
export const waitForElementById = async (find: () => HTMLElement | null, attempts?: number): Promise<HTMLElement | null> => {
    const startedIn = generation;
    const tries = attempts ?? (lazy ? 60 : 1);
    for (let i = 0; i < tries; i++) {
        await Vue.nextTick();
        // Checked before looking: the same element id may exist on the board that replaced this one.
        if (startedIn !== generation) return null;
        const element = find();
        if (element) return element;
        await new Promise((resolve) => requestAnimationFrame(resolve));
    }
    return startedIn === generation ? find() : null;
};

export const waitForCardElement = (cardId: string, attempts?: number) => waitForElementById(() => findCardElement(cardId), attempts);

const onPointerDown = () => {
    pointerPressed = true;
};

const onPointerUp = () => {
    pointerPressed = false;
    // What became far during the gesture is released now, even if the pointer stays still.
    const expired = Date.now() - EVICT_AFTER_MS;
    for (const region of regions.values()) {
        if (!region.near && region.farSince > expired) region.farSince = expired;
    }
    schedule();
};

// A printout shows the whole board, so for its duration all of it is there.
const onBeforePrint = () => {
    printing = true;
    for (const region of regions.values()) {
        region.resident.value = true;
    }
};

const onAfterPrint = () => {
    printing = false;
    onPointerUp();
};

const sampleCardHeight = () => {
    sampleTimer = null;
    if (!active || !scroller) return;
    const cards = scroller.querySelectorAll('.subtask-card');
    const count = Math.min(cards.length, HEIGHT_SAMPLE_SIZE);
    // A board built in steps, or a view without stories in reach yet: look again later.
    if (count === 0) {
        sampleCardHeightSoon();
        return;
    }
    let sum = 0;
    for (let i = 0; i < count; i++) {
        sum += (cards[i] as HTMLElement).offsetHeight + CARD_GAP;
    }
    const average = Math.round(sum / count);
    if (average !== averageCardHeight.value) {
        averageCardHeight.value = average;
        withScrollAnchor(updateAllBodyHeights);
    }
};

const sampleCardHeightSoon = () => {
    if (sampleTimer === null) {
        sampleTimer = setTimeout(sampleCardHeight, HEIGHT_SAMPLE_DELAY_MS);
    }
};

/**
 * The arrow keys look for their target among elements, so the nearest lane with cards on each side of the active card has to be
 * mounted before a key is pressed. Read inside the anchors' watcher, so that a card added to or removed from a lane between them,
 * a task moved, an activity collapsed or a filter changed finds the new neighbours.
 */
const navigationNeighbours = (releaseId: string, taskId: string, add: (card: Pick<Subtask, 'releaseId' | 'activityId' | 'taskId'>) => void) => {
    const state = useStorymapStore().state;
    const activityOrder = new Map(state.activities.map((x) => [x.id, x.order]));
    const tasks = orderBy(getVisibleTasksForNav(state), [(x) => activityOrder.get(x.activityId), 'order']);
    const index = tasks.findIndex((x) => x.id === taskId);
    if (index < 0) return;
    const onlyMatching = isHideUnfilteredActive.value ? matchingSubtaskIdSet.value : null;
    for (const direction of [-1, 1]) {
        for (let i = index + direction; i >= 0 && i < tasks.length; i += direction) {
            if (getTaskRelease(releaseId, tasks[i].id).value.some((card) => !onlyMatching || onlyMatching.has(card.id))) {
                add({ releaseId, activityId: tasks[i].activityId, taskId: tasks[i].id });
                break;
            }
        }
    }
};

const applyAnchors = (value: string) => {
    anchors.clear();
    for (const entry of value ? value.split(',') : []) {
        const [, releaseId, activityId] = entry.split('|');
        const releaseKey = `release|${releaseId}`;
        const activityKey = `activity|${activityId}`;
        anchors.add(releaseKey);
        anchors.add(activityKey);
        // Selecting, adding, editing and the next arrow key must not wait for a frame. The card scrolls itself into view once mounted.
        getRegion(releaseKey).resident.value = true;
        getRegion(activityKey).resident.value = true;
    }
    schedule();
};

export const startBoardResidency = () => {
    generation++;
    active = false;
    regions.clear();
    anchors.clear();
    reveals.clear();
    pointerPressed = false;
    laneCounts = {};
    lanesByRelease = {};
    const mapId = useStorymapStore().state.id;
    if (laneHeightsMapId !== mapId) {
        laneHeightsMapId = mapId;
        laneHeightsByLayout.clear();
    }
    laneHeights = new Map();
    reconsiderLazyBoard();
    lazy = isLazyBoardEnabled();
    if (!lazy) return;

    const store = useStorymapStore();
    watchers = new Vue();

    // One watcher reads what every card would otherwise read for itself.
    watchers.$watch(
        () => {
            const entries: string[] = [];
            const add = (kind: string, card: Pick<Subtask, 'releaseId' | 'activityId' | 'taskId'>) => {
                entries.push(`${kind}|${card.releaseId}|${card.activityId}|${card.taskId}`);
            };
            for (const card of store.state.subtasks) {
                if (card.isActive) {
                    add('active', card);
                    navigationNeighbours(card.releaseId, card.taskId, (neighbour) => add('neighbour', neighbour));
                } else if (card.isNew || card.isTitleEditing) {
                    add('editing', card);
                }
            }
            // Horizontal arrow keys measure from the card the move started at.
            const original = store.state.navigationState.originalSubtask;
            if (original) {
                add('original', original);
            }
            return entries.join(',');
        },
        applyAnchors,
        { immediate: true } as any
    );

    watchers.$watch(
        () => {
            const state = store.state;
            const collapsed = new Set(state.activities.filter((x) => x.isCollapsed).map((x) => x.id));
            const onlyMatching = isHideUnfilteredActive.value ? matchingSubtaskIdSet.value : null;
            const lanes: Record<string, number> = {};
            for (const card of state.subtasks) {
                if (collapsed.has(card.activityId) || (onlyMatching && !onlyMatching.has(card.id))) continue;
                const lane = `${card.releaseId}|${card.taskId}`;
                lanes[lane] = (lanes[lane] || 0) + 1;
            }
            const options = [state.discoveryViewOptions, state.planningViewOptions, state.backlogViewOptions];
            return JSON.stringify({ layout: [state.zoomLevel, state.currentViewType, options], lanes });
        },
        (value: string, previous: string) => {
            const { layout, lanes } = JSON.parse(value);
            const layoutChanged = !previous || JSON.stringify(JSON.parse(previous).layout) !== JSON.stringify(layout);
            if (layoutChanged) {
                const key = JSON.stringify(layout);
                laneHeights = laneHeightsByLayout.get(key) || new Map();
                laneHeightsByLayout.set(key, laneHeights);
                sampleCardHeightSoon();
            }
            laneCounts = lanes;
            lanesByRelease = {};
            for (const lane of Object.keys(lanes)) {
                const releaseId = lane.slice(0, lane.indexOf('|'));
                (lanesByRelease[releaseId] = lanesByRelease[releaseId] || []).push(lane);
            }
            for (const release of store.state.releases) getRegion(`release|${release.id}`);
            withScrollAnchor(updateAllBodyHeights);
            measureMountedLanesSoon();
        },
        { immediate: true } as any
    );
};

export const activateBoardResidency = () => {
    if (!lazy) return;
    scroller = document.querySelector('.storymap-scroll-container');
    (scroller as HTMLElement).style.overflowAnchor = 'none';
    releaseObserver = new IntersectionObserver(onIntersections, { root: scroller, rootMargin: RELEASE_MARGIN });
    activityObserver = new IntersectionObserver(onIntersections, { root: scroller, rootMargin: ACTIVITY_MARGIN });
    laneResizeObserver = new ResizeObserver(() => measureMountedLanesSoon());
    widthObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
            const region = elementRegions.get(entry.target);
            if (region) region.width.value = (entry.target as HTMLElement).offsetWidth;
        }
    });
    active = true;
    for (const region of regions.values()) {
        if (region.element) observe(region, region.element);
    }
    scroller.addEventListener('pointerdown', onPointerDown, true);
    scroller.addEventListener('wheel', onUserScrollInput, { passive: true, capture: true });
    scroller.addEventListener('touchmove', onUserScrollInput, { passive: true, capture: true });
    window.addEventListener('pointerup', onPointerUp, true);
    window.addEventListener('pointercancel', onPointerUp, true);
    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
    sampleCardHeightSoon();
    schedule();
};

export const stopBoardResidency = () => {
    generation++;
    active = false;
    if (frame !== null) cancelAnimationFrame(frame);
    frame = null;
    if (retryTimer !== null) clearTimeout(retryTimer);
    retryTimer = null;
    if (sampleTimer !== null) clearTimeout(sampleTimer);
    sampleTimer = null;
    releaseObserver?.disconnect();
    activityObserver?.disconnect();
    widthObserver?.disconnect();
    laneResizeObserver?.disconnect();
    laneResizeObserver = null;
    observedLanes.clear();
    releaseObserver = null;
    activityObserver = null;
    widthObserver = null;
    if (laneMeasureTimer !== null) clearTimeout(laneMeasureTimer);
    laneMeasureTimer = null;
    if (scroller) (scroller as HTMLElement).style.overflowAnchor = '';
    scroller?.removeEventListener('pointerdown', onPointerDown, true);
    scroller?.removeEventListener('wheel', onUserScrollInput, true);
    scroller?.removeEventListener('touchmove', onUserScrollInput, true);
    window.removeEventListener('pointerup', onPointerUp, true);
    window.removeEventListener('pointercancel', onPointerUp, true);
    window.removeEventListener('beforeprint', onBeforePrint);
    window.removeEventListener('afterprint', onAfterPrint);
    scroller = null;
    watchers?.$destroy();
    watchers = null;
    regions.clear();
    anchors.clear();
    reveals.clear();
    averageCardHeight.value = DEFAULT_CARD_HEIGHT;
};
